<script setup lang="ts">
import { ref, watch, onMounted, Ref, inject } from 'vue'
import { useRouter } from 'vue-router'
import { useGroups } from '@/services/Groups'
import { Group } from '@/types/Groups'
import { Member } from '@/types/Member'
import GroupTable from '@/components/GroupTable.vue'
import permissions from '@/services/permissions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

const router = useRouter()
const { checkPermission } = permissions()
const { getGroupTree, groupTree } = useGroups()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const loading = ref(true)

const totalGroups = ref(0)
const totalMedia = ref(0)
const totalMembers = ref(0)

const countGroups = (groups: Group[]): void => {
	let gCount = 0
	let mCount = 0
	let memCount = 0

	const traverse = (nodes: Group[]) => {
		for (const node of nodes) {
			gCount++
			mCount += node.mediaCount || 0
			memCount += node.memberCount || 0
			if (node.children && node.children.length > 0) {
				traverse(node.children)
			}
		}
	}

	traverse(groups)
	totalGroups.value = gCount
	totalMedia.value = mCount
	totalMembers.value = memCount
}

watch(groupTree, (newTree) => {
	if (newTree && newTree.length > 0) {
		countGroups(newTree)
	}
}, { deep: true })

onMounted(async () => {
	if (vhostId && vhostId.value) {
		await getGroupTree(vhostId.value)
		loading.value = false
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total Groups</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ totalGroups }}</div>
					<p class="text-xs text-muted-foreground">Folders and projects</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Media in Groups</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ totalMedia.toLocaleString() }}</div>
					<p class="text-xs text-muted-foreground">Files organized in groups</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Members in Groups</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ totalMembers.toLocaleString() }}</div>
					<p class="text-xs text-muted-foreground">Users assigned to groups</p>
				</CardContent>
			</Card>
		</div>

		<GroupTable />
	</div>
</template>
